/**
 * Location tags control — opens tag management (#999).
 * Primary placement: request toolbar (`variant="toolbar"`).
 * Native: full-screen host; web: centered dialog (Story 3.12 / UX-DR8).
 * Devices view toggle is a separate trailing toolbar control.
 */

import React, { useState } from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { LocationTagsManagementModal } from './LocationTagsManagementModal';
import { REQUEST_FORM_SIDEBAR_HEADER_CONTROL_HEIGHT } from '../../constants/requestFormLayout';
import { useOptionalLocationTagsDraft } from '../../contexts/LocationTagsDraftContext';
import { useRequestStage } from '../../contexts/RequestStageContext';
import { useLocationTagMutationAuthority } from '../../hooks/useLocationTagMutationAuthority';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { lineHeight } from '../../theme/typography';
import { isPersistedServerRequestId } from '../../utils/locationTagRequestId';
import { Layers, MapPin } from '../icons';

/** Toolbar toggle label for the Devices view (#999 Story 3.5). */
export const LOCATION_VIEW_TOGGLE_LABEL = 'View Devices';

/** @deprecated Use {@link LOCATION_VIEW_TOGGLE_LABEL}. */
export const LOCATION_VIEW_TOGGLE_ENTER_LABEL = LOCATION_VIEW_TOGGLE_LABEL;

/** Match `PrintPDFDropdown` trigger height on web. */
export const REQUEST_TOOLBAR_ACTION_HEIGHT = 36;

/** Runtime styles so Platform.OS is not frozen at StyleSheet.create time. */
function getToolbarButtonStyle() {
  const isWeb = Platform.OS === 'web';
  return {
    // Override sidebar/base minHeight so Tags/Devices match PDF density.
    minHeight: isWeb ? REQUEST_TOOLBAR_ACTION_HEIGHT : 28,
    height: isWeb ? REQUEST_TOOLBAR_ACTION_HEIGHT : undefined,
    paddingVertical: isWeb ? 0 : 6,
    paddingHorizontal: isWeb ? 12 : 8,
    // Shared with PDF, View Devices, Version History and Discussion.
    borderRadius: isWeb
      ? spacing.toolbarActionBorderRadius
      : spacing.toolbarActionBorderRadiusNative,
    gap: isWeb ? 6 : 4,
  };
}

function getToolbarButtonTextStyle() {
  const isWeb = Platform.OS === 'web';
  return {
    fontSize: isWeb ? 14 : 11,
    lineHeight: isWeb ? 18 : 14,
    fontWeight: '500' as const,
  };
}

export interface DevicesViewToolbarButtonProps {
  onPress: () => void;
  /** True while Devices view is active — keeps the same label, selected chrome. */
  selected?: boolean;
  disabled?: boolean;
  variant?: 'toolbar' | 'sidebar';
}

/**
 * Devices view toolbar toggle — sits to the right of PDF (#999).
 * Always labeled "View Devices"; selected while Devices is open.
 */
export const DevicesViewToolbarButton: React.FC<
  DevicesViewToolbarButtonProps
> = ({ onPress, selected = false, disabled = false, variant = 'toolbar' }) => {
  const isToolbar = variant === 'toolbar';
  // Keep usable while selected so the user can leave Devices even if recording ended mid-view.
  const buttonDisabled = disabled && !selected;
  const iconColor = buttonDisabled ? colors.textMuted : colors.primary;
  const iconSize = isToolbar ? (Platform.OS === 'web' ? 14 : 12) : 16;

  return (
    <TouchableOpacity
      style={[
        styles.button,
        isToolbar && getToolbarButtonStyle(),
        selected && styles.buttonSelected,
        buttonDisabled && styles.buttonDisabled,
      ]}
      onPress={onPress}
      disabled={buttonDisabled}
      testID="pd-location-view-button"
      accessibilityRole="button"
      accessibilityState={{ disabled: buttonDisabled, selected }}
      accessibilityLabel={LOCATION_VIEW_TOGGLE_LABEL}
      accessibilityHint={
        selected
          ? 'Leave Devices view and return to the previous step'
          : buttonDisabled
          ? 'Unavailable while recording'
          : 'View all devices grouped by location'
      }
    >
      <Layers size={iconSize} color={iconColor} />
      <Text
        style={[
          styles.buttonText,
          isToolbar && getToolbarButtonTextStyle(),
          selected && styles.buttonTextSelected,
          buttonDisabled && styles.buttonTextDisabled,
        ]}
        numberOfLines={1}
      >
        {LOCATION_VIEW_TOGGLE_LABEL}
      </Text>
    </TouchableOpacity>
  );
};

export interface LocationTagsControlProps {
  requestId?: string | null;
  requestStage?: string;
  requestorEmail?: string | null;
  disabled?: boolean;
  /** `toolbar` = request toolbar row (#999 Chapman); `sidebar` = legacy slot. */
  variant?: 'toolbar' | 'sidebar';
}

export const LocationTagsControl: React.FC<LocationTagsControlProps> = ({
  requestId,
  requestStage: requestStageProp,
  requestorEmail,
  disabled = false,
  variant = 'toolbar',
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const contextStage = useRequestStage();
  const requestStage = requestStageProp ?? contextStage;
  const draftContext = useOptionalLocationTagsDraft();
  const isServerMode = isPersistedServerRequestId(requestId);
  const isDraftMode = requestId === 'new-request' && draftContext != null;
  const { canMutate, isLoading: permissionsLoading } =
    useLocationTagMutationAuthority(requestId, requestStage, requestorEmail);
  const showControl = isServerMode || isDraftMode;

  if (!showControl || !requestId) {
    return null;
  }

  const buttonDisabled = disabled;
  const isToolbar = variant === 'toolbar';
  const iconSize = isToolbar ? (Platform.OS === 'web' ? 14 : 12) : 16;

  return (
    <>
      <View style={[styles.container, isToolbar && styles.containerToolbar]}>
        <TouchableOpacity
          style={[
            styles.button,
            isToolbar && getToolbarButtonStyle(),
            buttonDisabled && styles.buttonDisabled,
          ]}
          onPress={() => setModalVisible(true)}
          disabled={buttonDisabled}
          testID="pd-location-tags-button"
          accessibilityRole="button"
          accessibilityLabel="Manage location tags"
          accessibilityHint={
            canMutate
              ? 'Create, rename, or delete location tags'
              : 'View location tags for this request'
          }
        >
          <MapPin
            size={iconSize}
            color={buttonDisabled ? colors.textMuted : colors.primary}
          />
          <Text
            style={[
              styles.buttonText,
              isToolbar && getToolbarButtonTextStyle(),
              buttonDisabled && styles.buttonTextDisabled,
            ]}
            numberOfLines={1}
          >
            Location Tags
          </Text>
        </TouchableOpacity>
      </View>

      <LocationTagsManagementModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        requestId={requestId}
        canMutate={canMutate && !buttonDisabled}
        permissionsLoading={!isDraftMode && permissionsLoading}
        mode={isDraftMode ? 'draft' : 'server'}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    marginTop: spacing.sm,
  },
  containerToolbar: {
    width: 'auto',
    marginTop: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    minHeight: REQUEST_FORM_SIDEBAR_HEADER_CONTROL_HEIGHT,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: 8,
    paddingVertical: spacing.formCompactActionPaddingVertical,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  buttonSelected: {
    backgroundColor: colors.surfaceAccentMuted,
    borderColor: colors.primary,
  },
  buttonDisabled: {
    opacity: 0.55,
  },
  buttonText: {
    fontSize: 14,
    lineHeight: lineHeight.formControl,
    fontWeight: '500',
    color: colors.textPrimary,
  },
  buttonTextSelected: {
    color: colors.primary,
    fontWeight: '600',
  },
  buttonTextDisabled: {
    color: colors.textMuted,
  },
});
